import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Newspaper, Sparkles, RefreshCw, Plus, Trash2, 
  Copy, Check, Search, Globe, Clock, Settings, Zap,
  AlertCircle, Bot, SlidersHorizontal, ArrowLeft,
  Calendar, Layers, Tag, Compass
} from 'lucide-react';
import { sendWorkerAiRequest } from '../lib/workerAi';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import VoiceInputButton from './VoiceInputButton';
import { formatGregorianDateTime } from '../lib/dateUtils';
import { 
  NewsTopic, NewsState, getNewsState, saveNewsState, 
  subscribeToNewsData, syncNewsDataFromCloud 
} from '../lib/newsStorage';

export type { NewsTopic, NewsState };

const PRESET_SUGGESTIONS = [
  { key: 'newsPresetIsfahan', descKey: 'newsPresetIsfahanDesc', defaultTitle: 'اخبار رویدادها و تحولات شهری', defaultDesc: 'پایش اخبار شهری، پروژه‌ها و رویدادهای مهم منطقه‌ای', category: 'regional' as const },
  { key: 'newsPresetWulf', descKey: 'newsPresetWulfDesc', defaultTitle: 'سهام و دیتاسنترهای AI', defaultDesc: 'آخرین تحلیل قیمت، گزارش مالی و قراردادهای مرتبط', category: 'stocks' as const },
  { key: 'newsPresetAi', descKey: 'newsPresetAiDesc', defaultTitle: 'آخرین اخبار هوش مصنوعی و LLMها', defaultDesc: 'مدلهای جدید Gemini, OpenAI, Claude و ابزارهای AI', category: 'ai' as const },
  { key: 'newsPresetHardware', descKey: 'newsPresetHardwareDesc', defaultTitle: 'عینک‌های هوشمند و گجت‌های پوشیدنی', defaultDesc: 'گجت‌های پوشیدنی جدید مانند Ray-Ban Meta و Vision Pro', category: 'hardware' as const },
  { key: 'newsPresetSemiconductors', descKey: 'newsPresetSemiconductorsDesc', defaultTitle: 'بازار بورس فناوری و نیمه‌هادی‌ها', defaultDesc: 'تحلیل بازار چیپست‌ها و بورس بین‌الملل', category: 'stocks' as const }
];

export default function NewsModule() {
  const { lang, t } = useLanguage();
  const [newsState, setNewsState] = useState<NewsState>(() => getNewsState());

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Custom Topic Form inputs
  const [quickInputTopic, setQuickInputTopic] = useState('');
  const [newTopicDesc, setNewTopicDesc] = useState('');
  const [newTopicCategory, setNewTopicCategory] = useState<'stocks' | 'ai' | 'hardware' | 'regional' | 'general'>('general');
  const [copied, setCopied] = useState(false);

  // Sync with Cloudflare D1 in real-time across iOS, Android, and Web Chrome
  useEffect(() => {
    syncNewsDataFromCloud().then(cloudState => {
      if (cloudState) setNewsState(cloudState);
    }).catch(err => console.warn(err));

    const unsubscribe = subscribeToNewsData((updatedState) => {
      setNewsState(updatedState);
    });

    return () => unsubscribe();
  }, []);

  // Update news state & persist to Cloudflare D1
  const updateNewsState = (updater: NewsState | ((prev: NewsState) => NewsState)) => {
    setNewsState(prev => {
      const nextState = typeof updater === 'function' ? updater(prev) : updater;
      saveNewsState(nextState);
      return nextState;
    });
  };

  const enabledTopics = useMemo(() => {
    return newsState.topics.filter(t => t.enabled);
  }, [newsState.topics]);

  const getToneLabel = (tone: string) => {
    switch (tone) {
      case 'analytical':
        return t('newsToneAnalytical', '🎯 تحلیلی و مدیریتی (تمرکز بر تحلیل و فرصت‌ها)');
      case 'fast_summary':
        return t('newsToneFastSummary', '⚡ سریع و بولتی (فقط خلاصه‌های بسیار کوتاه)');
      case 'deep_technical':
        return t('newsToneDeepTech', '🔬 عمیق و تخصصی (بررسی جزئیات کامل)');
      case 'news_standard':
      default:
        return t('newsToneStandard', '📰 روزنامه‌نگاری و خبررسانی روان');
    }
  };

  // Add a topic
  const handleAddTopic = (titleText: string, descText?: string, cat?: any) => {
    const cleanTitle = titleText.trim();
    if (!cleanTitle) return;

    // Check if topic already exists
    if (newsState.topics.some(t => t.title.toLowerCase() === cleanTitle.toLowerCase())) {
      return;
    }

    const newTopic: NewsTopic = {
      id: `topic_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      title: cleanTitle,
      description: descText?.trim() || t('newsTopicLiveMonitorDesc', 'پایش زنده اخبار ۷ روز اخیر درباره {{topic}}', { topic: cleanTitle }),
      category: cat || 'general',
      enabled: true
    };

    updateNewsState(prev => ({
      ...prev,
      topics: [...prev.topics, newTopic]
    }));
  };

  // Handle Quick Add Submit from Top Input
  const handleQuickAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickInputTopic.trim()) return;

    const addedTitle = quickInputTopic.trim();
    const newTopicObj: NewsTopic = {
      id: `topic_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      title: addedTitle,
      description: newTopicDesc.trim() || t('newsTopicLiveMonitorDesc', 'پایش زنده اخبار ۷ روز اخیر درباره {{topic}}', { topic: addedTitle }),
      category: newTopicCategory,
      enabled: true
    };

    updateNewsState(prev => {
      if (prev.topics.some(t => t.title.toLowerCase() === addedTitle.toLowerCase())) {
        return prev;
      }
      return {
        ...prev,
        topics: [...prev.topics, newTopicObj]
      };
    });

    setQuickInputTopic('');
    setNewTopicDesc('');

    // Trigger AI Fetch immediately after adding if user pressed submit
    setTimeout(() => {
      handleFetchAiNewsWithTopics([...newsState.topics, newTopicObj]);
    }, 100);
  };

  // Fetch updated AI News
  const handleFetchAiNews = async () => {
    await handleFetchAiNewsWithTopics(enabledTopics);
  };

  const handleFetchAiNewsWithTopics = async (targetTopics: NewsTopic[]) => {
    const activeList = targetTopics.filter(t => t.enabled);
    if (activeList.length === 0) {
      setErrorMsg(t('newsErrorAddTopicFirst', 'لطفاً ابتدا حداقل یک موضوع مورد علاقه را وارد و اضافه کنید.'));
      return;
    }

    setIsLoading(true);
    setErrorMsg(null);

    const topicTitles = activeList.map(t => `- ${t.title} (${t.description})`).join('\n');
    const toneText = getToneLabel(newsState.personalityTone);

    const prompt = `
شما یک سیستم هوشمند پایش زنده اخبار و خبرنگار اختصاصی هستید.
وظیفه شما ارائه آخرین اخبار و تحولات **کاملاً واقعی، دقیق و جدید (مربوط به حداکثر ۷ روز اخیر / هفته گذشته)** بر اساس موضوعات دلخواه کاربر است.

قوانین بسیار مهم:
۱. **فقط اخبار ۷ روز اخیر**: به هیچ عنوان معرفی کلی، توضیحات عمومی، تاریخچه یا مطالب ویکی‌پدیاگونه ارائه ندهید. فقط رویدادها، تحولات، اتفاقات واقعی و اخبار داغ هفته اخیر را گزارش کنید.
۲. اگر موضوع کاربر یک شهر (مثلاً "اصفهان") است، اخبار واقعی ۷ روز اخیر اصفهان (مانند پروژه‌ها، رویدادهای شهری، هواشناسی، اقتصادی یا فرهنگی) را گزارش کنید.
۳. اگر موضوع کاربر سهام یا بورس است (مثلاً "TeraWulf" یا "WULF")، آخرین اخبار درآمدی، نوسانات قیمت، قراردادها و تحلیل ۷ روز اخیر سهم را گزارش کنید.
۴. اگر موضوع کاربر هوش مصنوعی یا عینک هوشمند است، آخرین رونمایی‌ها و اخبار واقعی هفته اخیر را ارائه دهید.
۵. **متن کاملاً خوانا و بدون کاراکترهای اضافی**: از کاراکترهای ستاره خام مانند ** در وسط عناوین ساده استفاده نکنید. متون را در قالب ساختار یافته و شکیل Markdown ارائه دهید تا کاملاً روان و خوانا باشد.

مشخصات کاربر و لحن:
- لحن گزارش: "${toneText}"
- موضوعات مورد علاقه کاربر:
${topicTitles}

ساختار خروجی (Markdown فارسی شکیل):
### 🌐 بولتن اختصاصی اخبار روز (هفته اخیر)

برای هر موضوع فعال:
#### 📌 [نام موضوع] (اخبار و رویدادهای ۷ روز اخیر)
* **تاریخ / زمان تقریبی:** [مثلاً: دیروز / ۲ روز پیش / این هفته]
* **شرح خبر و تحولات اخیر:** [گزارش دقیق، روان و جذاب از اتفاقات واقعی]
* **نکات کلیدی (Key Takeaways):**
  * [نکته ۱]
  * [نکته ۲]
  * [نکته ۳]
* **تحلیل یا وضعیت:** [ارزیابی کوتاه و کاربردی]

---

### 💡 جمع‌بندی و توصیه اختصاصی روز
[یک جمع‌بندی کوتاه، کاربردی و مدیریتی ۱ الی ۲ جمله‌ای متناسب با شخصیت کاربر]
`;

    try {
      const reply = await sendWorkerAiRequest('news_radar', prompt);
      const nowStr = formatGregorianDateTime(new Date(), lang);
      updateNewsState(prev => ({
        ...prev,
        lastBriefingMarkdown: reply,
        lastUpdatedTime: nowStr
      }));
    } catch (err: any) {
      setErrorMsg(err.message || t('newsErrorAiFetch', 'خطا در دریافت اخبار از هوش مصنوعی. لطفاً اتصال اینترنت را بررسی کنید.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTopic = (topicId: string) => {
    updateNewsState(prev => ({
      ...prev,
      topics: prev.topics.map(t => t.id === topicId ? { ...t, enabled: !t.enabled } : t)
    }));
  };

  const handleDeleteTopic = (topicId: string) => {
    updateNewsState(prev => ({
      ...prev,
      topics: prev.topics.filter(t => t.id !== topicId)
    }));
  };

  const handleCopyMarkdown = () => {
    if (!newsState.lastBriefingMarkdown) return;
    navigator.clipboard.writeText(newsState.lastBriefingMarkdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Filter markdown display if search is active
  const displayedMarkdown = useMemo(() => {
    if (!searchQuery.trim()) return newsState.lastBriefingMarkdown;

    const query = searchQuery.trim().toLowerCase();
    const lines = newsState.lastBriefingMarkdown.split('\n');
    const matchedLines = lines.filter(line => line.toLowerCase().includes(query));
    
    if (matchedLines.length === 0) {
      return t('newsNoSearchResults', '### 🔍 هیچ موردی متناسب با عبارت "{{query}}" یافت نشد.\n\nلطفاً کلمه دیگری را جستجو کنید یا دکمه بروزرسانی اخبار را فشار دهید.', { query: searchQuery });
    }
    return matchedLines.join('\n');
  }, [newsState.lastBriefingMarkdown, searchQuery, t]);

  return (
    <div id="news-module-container" className="space-y-6">
      
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-teal-600 text-white rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
        
        <div className="relative z-10 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold border border-white/30 flex items-center gap-1.5 backdrop-blur-xs">
                  <Zap className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />
                  {t('newsTitle', 'رادار اخبار و هوش مصنوعی')}
                </span>
                {newsState.lastUpdatedTime && (
                  <span className="text-[11px] text-indigo-100 flex items-center gap-1">
                    <Clock className="w-3 h-3 text-indigo-100" />
                    {t('date', 'تاریخ')}: {newsState.lastUpdatedTime}
                  </span>
                )}
              </div>

              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-display text-white">
                {t('newsHeaderTitle', 'رادار هوشمند اخبار، هوش مصنوعی و فناوری')}
              </h1>

              <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
                {t('newsHeaderDesc', 'پایش آخرین تحولات تکنولوژی، خلاصه‌سازی اختصاصی بولتن خبری با AI و تحلیل رویدادها')}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <button
                onClick={() => setShowSettingsModal(true)}
                className="px-4 py-2.5 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold transition-all border border-slate-700 flex items-center gap-2 shadow-sm cursor-pointer"
              >
                <Settings className="w-4 h-4 text-indigo-400" />
                <span>{t('newsSettingsToneBtn', 'تنظیمات و لحن')}</span>
              </button>

              {newsState.topics.length > 0 && (
                <button
                  onClick={handleFetchAiNews}
                  disabled={isLoading}
                  className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-indigo-500 to-emerald-500 hover:from-indigo-600 hover:to-emerald-600 text-white text-xs font-bold transition-all shadow-lg shadow-indigo-500/25 flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  <span>{isLoading ? t('newsFetchingStatus', 'در حال پایش و دریافت اخبار...') : t('newsUpdate7DaysBtn', 'بروزرسانی اخبار ۷ روز اخیر')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Quick Input Bar inside Hero Banner */}
          <form onSubmit={handleQuickAddSubmit} className="pt-2">
            <div className="flex flex-col sm:flex-row items-center gap-2 bg-slate-800/90 p-2 rounded-2xl border border-slate-700/80 shadow-inner">
              <div className="relative flex-1 w-full">
                <Search className="w-4 h-4 text-indigo-400 absolute right-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={quickInputTopic}
                  onChange={(e) => setQuickInputTopic(e.target.value)}
                  placeholder={t('newsQuickInputPlaceholder', 'موضوع دلخواه خود را تایپ کنید (مثلاً: اخبار فناوری، سهام دیتاسنترها، عینک هوشمند...)')}
                  className="w-full pr-10 pl-4 py-2.5 text-xs bg-slate-900/80 text-white placeholder-slate-400 rounded-xl border border-slate-700/60 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <VoiceInputButton
                currentValue={quickInputTopic}
                onUpdate={(val) => setQuickInputTopic(val)}
                disabled={isLoading}
                title={t('voiceInputNormal', 'ورودی صوتی')}
              />

              <button
                type="submit"
                disabled={!quickInputTopic.trim() || isLoading}
                className="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>{t('newsAddAndMonitorBtn', 'افزودن و پایش اخبار')}</span>
              </button>
            </div>
          </form>

          {/* Active Topics Pills */}
          <div className="pt-2 flex items-center gap-2 overflow-x-auto text-xs text-slate-300">
            <span className="text-gray-400 font-bold whitespace-nowrap flex items-center gap-1">
              <Globe className="w-3.5 h-3.5 text-indigo-400" />
              {t('newsYourTopics', 'موضوعات شما:')}
            </span>
            {newsState.topics.map(tItem => (
              <span key={tItem.id} className="px-3 py-1 rounded-xl bg-slate-800 text-indigo-200 border border-slate-700 whitespace-nowrap text-[11px] font-medium flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${tItem.enabled ? 'bg-emerald-400' : 'bg-gray-500'}`}></span>
                <span>{tItem.title}</span>
                <button
                  onClick={() => handleDeleteTopic(tItem.id)}
                  className="text-slate-400 hover:text-rose-400 text-xs mr-1 cursor-pointer"
                  title={t('delete', 'حذف')}
                >
                  ✕
                </button>
              </span>
            ))}
            {newsState.topics.length === 0 && (
              <span className="text-amber-300 text-[11px]">{t('newsNoTopicsHint', 'هنوز هیچ موضوعی اضافه نکرده‌اید. موضوع مورد نظرتان را در کادر بالا تایپ کنید!')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Error notification if any */}
      {errorMsg && (
        <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-rose-800 dark:text-rose-200 p-4 rounded-2xl text-xs flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
          <span className="flex-1">{errorMsg}</span>
          <button onClick={() => setErrorMsg(null)} className="text-xs text-rose-600 hover:underline cursor-pointer">{t('close', 'بستن')}</button>
        </div>
      )}

      {/* Preset Suggestions Bar if topics are few */}
      {newsState.topics.length < 3 && (
        <div className="bg-gradient-to-r from-indigo-50 to-emerald-50 dark:from-indigo-950/40 dark:to-emerald-950/40 p-4 rounded-2xl border border-indigo-100 dark:border-indigo-900/60 space-y-2">
          <p className="text-xs font-bold text-indigo-950 dark:text-indigo-200 flex items-center gap-1.5">
            <Compass className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            {t('newsQuickPresetsTitle', 'پیشنهادهای سریع برای اضافه کردن موضوع:')}
          </p>
          <div className="flex flex-wrap gap-2">
            {PRESET_SUGGESTIONS.map((preset, idx) => {
              const localizedTitle = t(preset.key, preset.defaultTitle);
              const localizedDesc = t(preset.descKey, preset.defaultDesc);
              return (
                <button
                  key={idx}
                  onClick={() => {
                    handleAddTopic(localizedTitle, localizedDesc, preset.category);
                  }}
                  className="px-3 py-1.5 rounded-xl bg-white dark:bg-gray-800 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 text-gray-700 dark:text-gray-200 text-xs font-medium border border-gray-200 dark:border-gray-700 transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer group"
                >
                  <Plus className="w-3.5 h-3.5 text-indigo-500 group-hover:text-white" />
                  <span>{localizedTitle}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* If No News fetched yet or No Topics */}
      {(!newsState.lastBriefingMarkdown || newsState.topics.length === 0) && !isLoading && (
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 sm:p-12 border border-gray-100 dark:border-gray-700 text-center space-y-6 shadow-sm">
          <div className="w-20 h-20 rounded-3xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mx-auto">
            <Newspaper className="w-10 h-10" />
          </div>

          <div className="max-w-md mx-auto space-y-2">
            <h3 className="text-base sm:text-lg font-extrabold text-gray-900 dark:text-white">
              {newsState.topics.length === 0 
                ? t('newsEmptyTitleNoTopics', 'موضوعات مورد علاقه خود را اضافه کنید')
                : t('newsEmptyTitleReady', 'آماده دریافت آخرین اخبار ۷ روز اخیر')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              {newsState.topics.length === 0
                ? t('newsEmptyDescNoTopics', 'کافیست موضوعات یا شهرهای مورد نظر خود را در کادر بالا وارد کنید تا اخبار داغ هفته اخیر را دریافت کنید.')
                : t('newsEmptyDescReady', 'بر روی دکمه زیر کلیک کنید تا هوش مصنوعی آخرین اخبار مربوط به موضوعات شما را پایش و تحلیل کند.')}
            </p>
          </div>

          {newsState.topics.length > 0 && (
            <button
              onClick={handleFetchAiNews}
              className="px-6 py-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-emerald-600 hover:from-indigo-700 hover:to-emerald-700 text-white text-xs font-bold shadow-lg shadow-indigo-500/20 inline-flex items-center gap-2 cursor-pointer transition-transform hover:scale-105"
            >
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span>{t('newsFetchAiBtn', 'پایش و دریافت اخبار ۷ روز اخیر با هوش مصنوعی')}</span>
            </button>
          )}
        </div>
      )}

      {/* Main Intelligence Reader Content */}
      {newsState.lastBriefingMarkdown && (
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 sm:p-8 border border-gray-100 dark:border-gray-700/80 shadow-sm relative">
          
          {/* Action Header */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pb-6 mb-6 border-b border-gray-100 dark:border-gray-700">
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('newsSearchPlaceholder', 'جستجو در اخبار دریافت شده...')}
                className="w-full pr-9 pl-4 py-2 rounded-xl text-xs bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              <button
                onClick={handleCopyMarkdown}
                className="px-3.5 py-2 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-gray-500" />}
                <span>{copied ? t('newsCopiedSuccess', 'کپی شد!') : t('newsCopyFullReport', 'کپی کل گزارش')}</span>
              </button>
            </div>
          </div>

          {isLoading && (
            <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/90 rounded-3xl z-20 flex flex-col items-center justify-center p-6 space-y-4 backdrop-blur-xs">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-indigo-200 dark:border-indigo-900 border-t-indigo-600 animate-spin" />
                <Bot className="w-8 h-8 text-indigo-600 dark:text-indigo-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-bold text-gray-900 dark:text-white">{t('newsAiAnalyzingWait', 'در حال پایش و دریافت آخرین اخبار ۷ روز اخیر با هوش مصنوعی...')}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('newsAiSearchingEvents', 'جستجوی رویدادها و تحولات تازه‌تر درباره موضوعات شما')}</p>
              </div>
            </div>
          )}

          <div className="markdown-body text-xs sm:text-sm leading-relaxed text-gray-800 dark:text-gray-100">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                table: ({ children }) => (
                  <div className="overflow-x-auto my-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xs">
                    <table className="w-full text-right text-xs border-collapse min-w-[320px]">
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead className="bg-indigo-50 dark:bg-indigo-950/80 text-indigo-950 dark:text-indigo-200 border-b border-gray-200 dark:border-gray-700 font-bold text-xs">
                    {children}
                  </thead>
                ),
                tbody: ({ children }) => (
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800/80 bg-white dark:bg-gray-900">
                    {children}
                  </tbody>
                ),
                tr: ({ children }) => (
                  <tr className="hover:bg-slate-50/80 dark:hover:bg-gray-800/50 transition-colors">
                    {children}
                  </tr>
                ),
                th: ({ children }) => (
                  <th className="py-2.5 px-4 font-bold text-right text-xs whitespace-nowrap text-indigo-950 dark:text-indigo-300">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="py-2.5 px-4 text-xs text-gray-700 dark:text-gray-200 font-sans border-t border-gray-100 dark:border-gray-800">
                    {children}
                  </td>
                ),
                h1: ({ children }) => (
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white my-4 border-b pb-2 border-gray-100 dark:border-gray-700 flex items-center gap-2">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-base font-bold text-indigo-600 dark:text-indigo-400 my-3 pt-2">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-sm font-bold text-emerald-600 dark:text-emerald-400 my-3 flex items-center gap-1.5">
                    {children}
                  </h3>
                ),
                h4: ({ children }) => (
                  <h4 className="text-xs sm:text-sm font-bold text-indigo-900 dark:text-indigo-200 my-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                    {children}
                  </h4>
                ),
                p: ({ children }) => (
                  <p className="mb-3 leading-relaxed text-gray-700 dark:text-gray-200">
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc list-inside space-y-1.5 my-3 text-xs sm:text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/60 p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal list-inside space-y-1.5 my-3 text-xs sm:text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/60 p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
                    {children}
                  </ol>
                ),
                strong: ({ children }) => (
                  <strong className="font-bold text-indigo-700 dark:text-indigo-300">
                    {children}
                  </strong>
                ),
                hr: () => <hr className="my-6 border-gray-100 dark:border-gray-800" />
              }}
            >
              {displayedMarkdown}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-xl w-full p-6 sm:p-8 space-y-6 max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 dark:border-gray-700">
            
            <div className="flex items-center justify-between border-b pb-4 border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-indigo-500" />
                <h3 className="text-base font-bold text-gray-900 dark:text-white">{t('newsSettingsModalTitle', 'تنظیمات موضوعات و لحن گزارش')}</h3>
              </div>
              <button 
                onClick={() => setShowSettingsModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs font-bold p-1 rounded-lg cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Select Personality Tone */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 dark:text-gray-300 block">
                {t('newsToneLabel', 'لحن و شخصیت گزارش اخبار:')}
              </label>
              <select
                value={newsState.personalityTone}
                onChange={(e) => updateNewsState(prev => ({ ...prev, personalityTone: e.target.value as any }))}
                className="w-full p-3 rounded-xl text-xs bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="analytical">{t('newsToneAnalytical', '🎯 تحلیلی و مدیریتی (تمرکز بر تحلیل و فرصت‌ها)')}</option>
                <option value="fast_summary">{t('newsToneFastSummary', '⚡ سریع و بولتی (فقط خلاصه‌های بسیار کوتاه)')}</option>
                <option value="deep_technical">{t('newsToneDeepTech', '🔬 عمیق و تخصصی (بررسی جزئیات کامل)')}</option>
                <option value="news_standard">{t('newsToneStandard', '📰 روزنامه‌نگاری و خبررسانی روان')}</option>
              </select>
            </div>

            {/* List of Topics */}
            <div className="space-y-3">
              <label className="text-xs font-bold text-gray-700 dark:text-gray-300 block">
                {t('newsActiveTopicsLabel', 'موضوعات فعال برای پایش زنده اخبار:')}
              </label>
              
              {newsState.topics.length === 0 ? (
                <p className="text-xs text-gray-500 italic p-3 text-center bg-gray-50 dark:bg-gray-900 rounded-xl">{t('newsNoTopicsHint', 'هنوز موضوعی اضافه نشده است.')}</p>
              ) : (
                <div className="space-y-2.5">
                  {newsState.topics.map(tItem => (
                    <div 
                      key={tItem.id} 
                      className={`p-3.5 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
                        tItem.enabled 
                          ? 'bg-indigo-50/50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800' 
                          : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-800 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <input 
                          type="checkbox"
                          checked={tItem.enabled}
                          onChange={() => handleToggleTopic(tItem.id)}
                          className="w-4 h-4 text-indigo-600 rounded-md focus:ring-indigo-500 cursor-pointer"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-gray-900 dark:text-white truncate">{tItem.title}</p>
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{tItem.description}</p>
                        </div>
                      </div>

                      <button
                        onClick={() => handleDeleteTopic(tItem.id)}
                        className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition-colors cursor-pointer"
                        title={t('newsDeleteTopicTooltip', 'حذف موضوع')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-5 py-2 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-bold hover:opacity-90 transition-opacity cursor-pointer"
              >
                {t('newsSaveAndConfirmBtn', 'ذخیره و تایید')}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
